import { useState, useEffect } from 'react';
import { AlertTriangle, ArrowDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface EscalateTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketNumber: string;
  mode: 'escalate' | 'deescalate';
  onConfirm: (data: { escalated: boolean; escalationReason?: string }) => Promise<void>;
  updating: boolean;
}

export function EscalateTicketDialog({ open, onOpenChange, ticketNumber, mode, onConfirm, updating }: EscalateTicketDialogProps) {
  const { t } = useTranslation('tickets');
  const [reason, setReason] = useState('');
  const isEscalate = mode === 'escalate';

  // Reset form when dialog opens
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEscalate
              ? <AlertTriangle className="w-5 h-5 text-brand-orange" />
              : <ArrowDown className="w-5 h-5 text-green-600" />
            }
            {isEscalate ? t('escalateDialog.escalateTitle') : t('escalateDialog.deescalateTitle')}
          </DialogTitle>
          <DialogDescription>
            {isEscalate
              ? t('escalateDialog.escalateDesc', { ticket: ticketNumber })
              : t('escalateDialog.deescalateDesc', { ticket: ticketNumber })
            }
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="escalation-reason">{t('escalateDialog.reasonLabel')}</Label>
          <Textarea
            id="escalation-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1000}
            placeholder={isEscalate ? t('escalateDialog.escalatePlaceholder') : t('escalateDialog.deeescalatePlaceholder')}
            rows={3}
            disabled={updating}
          />
          <p className="text-xs text-slate-400 text-right">{reason.length}/1000</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updating}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={() =>
              onConfirm({
                escalated: isEscalate,
                ...(reason.trim() ? { escalationReason: reason.trim() } : {}),
              })
            }
            disabled={updating}
            className={isEscalate
              ? 'bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'
            }
          >
            {updating && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {isEscalate
              ? (updating ? t('escalateDialog.escalating') : t('escalateDialog.escalateBtn'))
              : (updating ? t('escalateDialog.deescalating') : t('escalateDialog.deescalateBtn'))
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
